import React, { useContext } from 'react'
import { AppBar, Toolbar, Drawer, Button, CssBaseline, List, ListItemButton, ListItemText, ListItemIcon, Divider, Tooltip } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles';
import { Link, useResolvedPath } from 'react-router'
import SelectDataRoom from './SelectDataRoom'
import MessageService from './MessageService'
import { DataRoomsContext, StatusContext } from './Contexts'
import { TableLargeIcon, SimpleTableIcon } from './Icons'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ForumIcon from '@mui/icons-material/Forum'
import EventNoteIcon from '@mui/icons-material/EventNote'
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import HelpIcon from '@mui/icons-material/Help'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import { useSelectedDataRoom } from 'hooks/useSelectedDataRoom'
import Logo from "./logo";

const drawerWidth = 240

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    backgroundImage: "linear-gradient(to right bottom in oklab, rgb(235, 235, 235) 0%, rgb(250, 250, 250) 100%);",
  },
  appBar: {
    width: '100%',
    zIndex: theme.zIndex.drawer + 1,
    marginLeft: drawerWidth,
    flexGrow: 1,
    boxShadow: 'none',
    backgroundColor: '#14447b'
  },
  toolbar: {
    alignItems: "center",
  },
  logo: {
    fill: "white",
  },
  logoContainer: {
    flexGrow: 1,
    paddingTop: 6,
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    paddingTop: 80,
    minHeight: '100vh',
  },
  list: {
    '& a': {
      color: 'unset',
      textDecoration: 'none'
    },
    paddingTop: "68px",
  },
  listItem: {
    flexWrap: 'nowrap'
  },
  roomName: {
    display: 'flex',
    marginLeft: 10,
    fontSize: 14,
  },
  roomIcon: {
    marginLeft: 10,
    marginTop: -2,
    fontSize: 18,
  },
  listDataRoomsLink: {
    marginLeft: 2,
    fontStyle: 'italic',
    fontSize: 14,
  },
  listDataRoomsIcon: {
    marginLeft: -4,
    marginTop: -2,
    fontSize: 18,
  },
  listDataRoomsItemIcon: {
    minWidth: 0,
    marginRight: 2,
  }
}))

const onLogoutClick = () => {
  window.location.replace('/logout')
}

function Layout({ children, showRoomSelect = true }) {
  const classes = useStyles()
  const { dataRoom } = useSelectedDataRoom()
  const { dataRooms } = useContext(DataRoomsContext)
  const { sessionStatus } = useContext(StatusContext)
  const showRedaction = dataRooms.some(
    dataRoom => dataRoom.redaction || dataRoom.allow_redaction
  )
  
  const {pathname:page} = useResolvedPath('')

  return (
    <div className={classes.root}>
      <CssBaseline />

      <Drawer
        className={classes.drawer}
        variant='permanent'
        classes={{
          paper: classes.drawerPaper,
        }}
        anchor='left'
        open={true}
      >
        <List className={classes.list} >
          <Link to={dataRoom && dataRoom.data_room ? `/data-rooms/${dataRoom.data_room}` : '/data-rooms/'}>
            <ListItemButton className={classes.listItem} selected={page.match(/^\/data-rooms\/.*/)}>
              <ListItemIcon>
                <TableLargeIcon />
              </ListItemIcon>
              <ListItemText primary='Data Rooms' />
            </ListItemButton>
          </Link>
          {dataRoom && <Link to={`/data-room/${dataRoom.data_room}`}>
            <ListItemButton className={classes.listItem} selected={page.match(/^\/data-room\/.*/)}>
              <ListItemIcon>
                <SimpleTableIcon className={classes.roomIcon}/>
              </ListItemIcon>
              <ListItemText primary={dataRoom.data_room_display_name} classes={{primary: classes.roomName}}/>
            </ListItemButton>
          </Link>}
          <Link to={dataRoom && dataRoom.data_room ? `/user-rights/${dataRoom.data_room}` : '/user-rights/'}>
            <ListItemButton selected={page.match(/^\/user-rights\/.*/)}>
              <ListItemIcon>
                <SupervisorAccountIcon />
              </ListItemIcon>
              <ListItemText primary='User Rights' />
            </ListItemButton>
          </Link>
          {showRedaction && <Link to={dataRoom && dataRoom.data_room ? `/redaction/${dataRoom.data_room}` : '/redaction/'}>
            <ListItemButton selected={page.match(/^\/redaction\/.*/)}>
              <ListItemIcon>
                <VisibilityOffIcon />
              </ListItemIcon>
              <ListItemText primary='Redaction [BETA]' />
            </ListItemButton>
          </Link>}
          <Link to={dataRoom && dataRoom.data_room ? `/qa/${dataRoom.data_room}` : '/qa/'}>
            <ListItemButton selected={page.match(/^\/qa\/.*/)}>
              <ListItemIcon>
                <ForumIcon />
              </ListItemIcon>
              <ListItemText primary='Q&A' />
            </ListItemButton>
          </Link>
          <Link to={dataRoom && dataRoom.data_room ? `/statistics/${dataRoom.data_room}` : '/statistics/'}>
            <ListItemButton selected={page.match(/^\/statistics\/.*/)}>
              <ListItemIcon>
                <EventNoteIcon />
              </ListItemIcon>
              <ListItemText primary='Statistics' />
            </ListItemButton>
          </Link>
          {sessionStatus.is_billing_admin && <Link to='/billing/'>
            <ListItemButton selected={page.match(/^\/billing\/.*/) || page.match(/^\/billing-data-room\/.*/)}>
              <ListItemIcon>
                <AttachMoneyIcon />
              </ListItemIcon>
              <ListItemText primary='Billing' />
            </ListItemButton>
          </Link>}
          <Link to={dataRoom && dataRoom.data_room ? `/access/${dataRoom.data_room}` : '/access/'}>
            <ListItemButton selected={page.match(/^\/access\/.*/)}>
              <ListItemIcon>
                <OpenInBrowserIcon />
              </ListItemIcon>
              <ListItemText primary='Access Data Room' />
            </ListItemButton>
          </Link>
          <a href='https://docs.aversure.com' target='_blank' rel='noopener noreferrer'>
            <ListItemButton selected={page.match(/^\/support\/.*/)}>
              <ListItemIcon>
                <HelpIcon />
              </ListItemIcon>
              <ListItemText primary='Documentation and Support' />
            </ListItemButton>
          </a>

          <div style={{height: 48}}/>
          <Divider/>

          <Tooltip title='Including data rooms that you are not administrator of' placement='top-start'>
            <a href='/go/list_data_rooms' target='_blank'>
              <ListItemButton>
                <ListItemText primary='List all Data Rooms' classes={{ primary: classes.listDataRoomsLink }}/>
                <ListItemIcon className={classes.listDataRoomsItemIcon}>
                  <FormatListBulletedIcon className={classes.listDataRoomsIcon}/>
                </ListItemIcon>
              </ListItemButton>
            </a>
          </Tooltip>

        </List>
      </Drawer>

      <AppBar className={classes.appBar} position='fixed'>
        <Toolbar className={classes.toolbar} style={{minHeight: 60}}>
          <div className={classes.logoContainer}>
            <Logo className={classes.logo} height={20} />
          </div>
          {showRoomSelect && <SelectDataRoom />}
          <MessageService />
          <Button color='inherit' startIcon={<ExitToAppIcon/>} onClick={onLogoutClick}>Log out</Button>
        </Toolbar>
      </AppBar>

      <div className={classes.content}>
        {children}
      </div>
    </div>
  )
}

export default Layout
